import { IDeviceListItem } from "interfaces/entities/devices";
import {
  IEntityCreateFailure,
  IEntityDeleteSuccess,
  RequestAction,
  createEntityActionNames,
  createEntityActions,
  createRequestTypes,
} from "store/entities/actions";
import { ActionsUnion, IAction, IActionWithPayload, createAction } from "store/helper";
import { Theme } from "themes/helpers";

import {
  IAgent,
  IBatchCreateAgentsFailurePayload,
  IBatchCreateAgentsPayload,
  IBatchCreateAgentsSuccessPayload,
  IChangeAutoAssignmentSuccessPayload,
  IFinishWelcomeTutorialPayload,
  ISyncDeleteAgentPayload,
  IUpdateAgentPayload,
} from "./interfaces";

export const AgentActionsNames = {
  AGENT: "AGENT",
  CREATE_BATCH: "ENTITIES/AGENT/CREATE_BATCH",
  SYNC_DELETE: "ENTITIES/AGENT/SYNC_DELETE",
  FINISH_WELCOME_TUTORIAL: "ENTITIES/AGENT/FINISH_WELCOME_TUTORIAL",
  SET_FLAG: "ENTITIES/AGENT/SET_FLAG",
  SEND_PUSH_TEST: "ENTITIES/AGENT/SEND_PUSH_TEST",
  SUBSCRIBE_DEVICE: "ENTITIES/AGENT/SUBSCRIBE_DEVICE",
  UNSUBSCRIBE_DEVICE: "ENTITIES/AGENT/UNSUBSCRIBE_DEVICE",
  SET_LAST_CLOSED_GOOGLE_VERIFICATION: "ENTITIES/AGENT/SET_LAST_CLOSED_GOOGLE_VERIFICATION",
  CHANGE_THEME: "ENTITIES/AGENT/CHANGE_THEME",
  CHANGE_AUTO_ASSIGNMENT: "ENTITIES/AGENT/CHANGE_AUTO_ASSIGNMENT",
};

export const AGENT = {
  ...createEntityActionNames(AgentActionsNames.AGENT),
  CREATE_BATCH: createRequestTypes(AgentActionsNames.CREATE_BATCH),
  SET_FLAG: createRequestTypes(AgentActionsNames.SET_FLAG),
  SEND_PUSH_TEST: createRequestTypes(AgentActionsNames.SEND_PUSH_TEST),
  SUBSCRIBE_DEVICE: createRequestTypes(AgentActionsNames.SUBSCRIBE_DEVICE),
  UNSUBSCRIBE_DEVICE: createRequestTypes(AgentActionsNames.UNSUBSCRIBE_DEVICE),
  FINISH_WELCOME_TUTORIAL: createRequestTypes(AgentActionsNames.FINISH_WELCOME_TUTORIAL),
  SET_LAST_CLOSED_GOOGLE_VERIFICATION: createRequestTypes(AgentActionsNames.SET_LAST_CLOSED_GOOGLE_VERIFICATION),
  CHANGE_THEME: createRequestTypes(AgentActionsNames.CHANGE_THEME),
  CHANGE_AUTO_ASSIGNMENT: createRequestTypes(AgentActionsNames.CHANGE_AUTO_ASSIGNMENT),
};

export const AgentSelectorsNames = {
  CREATE_BATCH: AgentActionsNames.CREATE_BATCH,
};

const createBatchActions = {
  batchCreateAgents: (payload: IBatchCreateAgentsPayload): IActionWithPayload<string, IBatchCreateAgentsPayload> =>
    createAction(AGENT.CREATE_BATCH[RequestAction.REQUEST], payload),
  batchCreateAgentsSuccess: (
    payload: IBatchCreateAgentsSuccessPayload
  ): IActionWithPayload<string, IBatchCreateAgentsSuccessPayload> =>
    createAction(AGENT.CREATE_BATCH[RequestAction.SUCCESS], payload),

  batchCreateAgentsFailure: (
    payload: IBatchCreateAgentsFailurePayload
  ): IActionWithPayload<string, IEntityCreateFailure> =>
    createAction(AGENT.CREATE_BATCH[RequestAction.FAILURE], payload),
};

const changeThemeActions = {
  changeTheme: (payload: { theme: Theme }): IAction => createAction(AGENT.CHANGE_THEME[RequestAction.REQUEST], payload),
  changeThemeSuccess: (payload: { agent: IAgent }): IAction =>
    createAction(AGENT.CHANGE_THEME[RequestAction.SUCCESS], payload),
  changeThemeFailure: (): IAction => createAction(AGENT.CHANGE_THEME[RequestAction.FAILURE]),
};

const changeAutoAssignmentActions = {
  changeAutoAssignment: (payload: { autoassignment: boolean }): IAction =>
    createAction(AGENT.CHANGE_AUTO_ASSIGNMENT[RequestAction.REQUEST], payload),
  changeAutoAssignmentSuccess: (payload: IChangeAutoAssignmentSuccessPayload): IAction =>
    createAction(AGENT.CHANGE_AUTO_ASSIGNMENT[RequestAction.SUCCESS], payload),
  changeAutoAssignmentFailure: (): IAction => createAction(AGENT.CHANGE_AUTO_ASSIGNMENT[RequestAction.FAILURE]),
};

export const AgentActions = {
  ...createEntityActions<IAgent>(AGENT),
  update: (payload: IUpdateAgentPayload): IActionWithPayload<string, IUpdateAgentPayload> =>
    createAction(AGENT.UPDATE[RequestAction.REQUEST], payload),
  ...createBatchActions,
  ...changeThemeActions,
  ...changeAutoAssignmentActions,
  syncDelete: (payload: ISyncDeleteAgentPayload): IActionWithPayload<string, IEntityDeleteSuccess> =>
    createAction(AgentActionsNames.SYNC_DELETE, payload),
  finishWelcomeTutorial: (payload: IFinishWelcomeTutorialPayload): IActionWithPayload<string, Partial<IAgent>> =>
    createAction(AGENT.FINISH_WELCOME_TUTORIAL[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setFlag: (payload: { name: string; value: boolean | string }) =>
    createAction(AGENT.SET_FLAG[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setFlagSuccess: (payload: { id: string; name: string; value: boolean | string }) =>
    createAction(AGENT.SET_FLAG[RequestAction.SUCCESS], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setFlagFailure: () => createAction(AGENT.SET_FLAG[RequestAction.FAILURE], {}),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  sendPushTest: (payload: { agentId: string; deviceId: string }) =>
    createAction(AGENT.SEND_PUSH_TEST[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  subscribeAgentDevice: (payload: { subscription: Record<string, unknown> }) =>
    createAction(AGENT.SUBSCRIBE_DEVICE[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  subscribeAgentDeviceSuccess: (payload: { agentId: string; deviceSubscription: IDeviceListItem }) =>
    createAction(AGENT.SUBSCRIBE_DEVICE[RequestAction.SUCCESS], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  subscribeAgentDeviceFailure: () => createAction(AGENT.SUBSCRIBE_DEVICE[RequestAction.FAILURE], {}),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  unsubscribeAgentDevice: (payload: { deviceId: string }) =>
    createAction(AGENT.UNSUBSCRIBE_DEVICE[RequestAction.REQUEST], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  unsubscribeAgentDeviceSuccess: (payload: { agentId: string; deviceId: string }) =>
    createAction(AGENT.UNSUBSCRIBE_DEVICE[RequestAction.SUCCESS], payload),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  unsubscribeAgentDeviceFailure: () => createAction(AGENT.UNSUBSCRIBE_DEVICE[RequestAction.FAILURE], {}),
  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  setLastClosedGoogleVerification: (payload: { id: string }) =>
    createAction(AGENT.SET_LAST_CLOSED_GOOGLE_VERIFICATION[RequestAction.REQUEST], payload),
};

export type AgentAction = ActionsUnion<typeof AgentActions>;
